import { useState } from 'react'
import { useApiFetch } from '../../hooks/useApiFetch.js'
import { StatCard, Card, Spinner, Alert, Select } from '../../components/ui/index.js'
import { DonutChart, BarChart, LineChart } from '../../components/charts/index.js'
import { CHART_COLORS } from '../../components/charts/chartTokens.js'
import { formatNumber, formatCurrency } from '../../lib/format.js'
import styles from './AdminDashboard.module.css'

interface StatsData {
  users: { total: number; premium: number; newThisMonth: number }
  ai: { totalGenerations: number; totalCost: number; thisMonthGenerations: number; thisMonthCost: number }
  aiThemes: { totalThemes: number; totalCost: number; thisMonthThemes: number; thisMonthCost: number; safelistFailRate: number }
  revenue: { totalAmount: number; thisMonthAmount: number; completedPayments: number }
  invitations: { total: number; published: number; draft: number }
}

interface AiDailyStats {
  dailyStats: { date: string; count: number; cost: number }[]
}

const now = new Date()
const currentYear = now.getFullYear()
const currentMonth = now.getMonth() + 1

function buildMonthOptions() {
  const options: { value: string; label: string }[] = []
  for (let m = currentMonth; m >= 1; m--) {
    options.push({ value: `${currentYear}-${m}`, label: `${currentYear}년 ${m}월` })
  }
  // 작년 12월부터 1월까지
  for (let m = 12; m >= 1; m--) {
    options.push({ value: `${currentYear - 1}-${m}`, label: `${currentYear - 1}년 ${m}월` })
  }
  return options
}

const monthOptions = buildMonthOptions()

export function AdminDashboard() {
  const [selectedMonth, setSelectedMonth] = useState(`${currentYear}-${currentMonth}`)
  const [year, month] = selectedMonth.split('-')

  const { data, isLoading, error } = useApiFetch<StatsData>('/api/admin/stats')
  const { data: aiDaily, isLoading: aiDailyLoading } = useApiFetch<AiDailyStats>(
    `/api/admin/stats/ai-daily?year=${year}&month=${month}`,
  )

  if (isLoading) {
    return (
      <div className={styles.loadingWrap}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className={styles.root}>
        <div className={styles.statGrid}>
          <StatCard label="총 유저" value="-" />
          <StatCard label="AI 생성" value="-" />
          <StatCard label="이번달 매출" value="-" />
          <StatCard label="청첩장" value="-" />
          <StatCard label="총 매출" value="-" />
        </div>
        {error && <Alert variant="danger">데이터를 불러올 수 없습니다: {error}</Alert>}
      </div>
    )
  }

  const freeUsers = data.users.total - data.users.premium
  const themeSuccessCount = data.aiThemes.totalThemes - Math.round(data.aiThemes.totalThemes * data.aiThemes.safelistFailRate / 100)
  const themeFailCount = Math.round(data.aiThemes.totalThemes * data.aiThemes.safelistFailRate / 100)

  return (
    <div className={styles.root}>
      <div className={styles.statGrid}>
        <StatCard label="총 유저" value={formatNumber(data.users.total)} change={`+${data.users.newThisMonth} 이번달`} />
        <StatCard label="AI 생성" value={formatNumber(data.ai.totalGenerations)} change={`+${data.ai.thisMonthGenerations} 이번달`} />
        <StatCard label="이번달 매출" value={formatCurrency(data.revenue.thisMonthAmount)} />
        <StatCard label="청첩장" value={formatNumber(data.invitations.total)} />
        <StatCard label="총 매출" value={formatCurrency(data.revenue.totalAmount)} change={`${data.revenue.completedPayments}건 완료`} />
      </div>

      <Card
        title="일별 AI 생성 추이"
        extra={
          <Select
            size="sm"
            options={monthOptions}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
        }
      >
        <div className={styles.dailyChartWrap}>
          {aiDailyLoading ? (
            <div className={styles.loadingWrap}><Spinner /></div>
          ) : aiDaily && aiDaily.dailyStats.length > 0 ? (
            <LineChart
              series={[{
                key: 'generations',
                label: 'AI 생성 건수',
                data: aiDaily.dailyStats.map((d) => ({ date: d.date, value: d.count })),
                color: CHART_COLORS.primary,
              }]}
              areaFill
            />
          ) : (
            <div className={styles.emptyChart}>데이터가 없습니다</div>
          )}
        </div>
      </Card>

      <div className={styles.chartGrid}>
        <Card title="유저 플랜 분포">
          <div className={styles.chartWrap}>
            <DonutChart
              data={[
                { label: 'FREE', value: freeUsers, color: CHART_COLORS.gray },
                { label: 'PREMIUM', value: data.users.premium, color: CHART_COLORS.info },
              ]}
            />
          </div>
          <div className={styles.chartLegend}>
            <span className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: CHART_COLORS.gray }} />
              FREE ({freeUsers})
            </span>
            <span className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: CHART_COLORS.info }} />
              PREMIUM ({data.users.premium})
            </span>
          </div>
        </Card>

        <Card title="청첩장 상태">
          <div className={styles.chartWrap}>
            <DonutChart
              data={[
                { label: 'PUBLISHED', value: data.invitations.published, color: CHART_COLORS.success },
                { label: 'DRAFT', value: data.invitations.draft, color: CHART_COLORS.warning },
              ]}
            />
          </div>
          <div className={styles.chartLegend}>
            <span className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: CHART_COLORS.success }} />
              발행 ({data.invitations.published})
            </span>
            <span className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: CHART_COLORS.warning }} />
              초안 ({data.invitations.draft})
            </span>
          </div>
        </Card>

        <Card title="매출 vs AI 비용">
          <div className={styles.chartWrap}>
            <BarChart
              data={[
                { label: '매출', value: data.revenue.totalAmount, color: CHART_COLORS.success },
                { label: 'AI 비용', value: Math.round((data.ai.totalCost + data.aiThemes.totalCost) * 1400), color: CHART_COLORS.danger },
              ]}
            />
          </div>
        </Card>

        <Card title="AI 테마 성공률">
          <div className={styles.chartWrap}>
            <DonutChart
              data={[
                { label: '성공', value: themeSuccessCount, color: CHART_COLORS.success },
                { label: '실패', value: themeFailCount, color: CHART_COLORS.danger },
              ]}
            />
          </div>
          <div className={styles.chartLegend}>
            <span className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: CHART_COLORS.success }} />
              성공
            </span>
            <span className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: CHART_COLORS.danger }} />
              실패 ({data.aiThemes.safelistFailRate}%)
            </span>
          </div>
        </Card>
      </div>
    </div>
  )
}
